import { Injectable } from '@angular/core';
import { HttpService } from './http.service';
import { InitConfig } from '../models/initConfig.model';
import { Filters } from '../models/filters.model';
import { ToastService } from './toast.service';
import { CapacitorMusicControls } from 'capacitor-music-controls-plugin';

import { Environment } from 'src/environments/environment';

@Injectable({ providedIn: 'root' })
export class PodcastService {
  public podcastConfig: InitConfig;
  private playingEpisode = null;
  public audio: HTMLAudioElement = null;

  constructor(private httpService: HttpService, public toast: ToastService) {}

  create(episode) {
    if (this.audio) {
      this.audio.pause();
      this.audio = null;
    }
    this.audio = new Audio(episode.url);
    
    // Configurar eventos del audio
    this.audio.addEventListener('loadedmetadata', () => {
      console.log('Audio metadata loaded, duration:', this.audio.duration);
    });
    
    this.audio.addEventListener('timeupdate', () => {
      // Actualizar controles de música con el progreso
      if (this.audio && !isNaN(this.audio.duration)) {
        this.updateElapsed(this.audio.currentTime, !this.audio.paused);
      }
    });
    
    this.audio.addEventListener('ended', () => {
      console.log('Audio ended');
      this.destroyMusicControls();
    });
    
    return this.audio;
  }

  async createControls(episode) {
    try {
      // Obtener duración del episodio si está disponible
      let duration = 0;
      let elapsed = 0;
      
      if (this.audio) {
        try {
          // Esperar a que se cargue la metadata si no está disponible
          if (isNaN(this.audio.duration) || this.audio.duration === 0) {
            await new Promise((resolve) => {
              const onLoadedMetadata = () => {
                this.audio.removeEventListener('loadedmetadata', onLoadedMetadata);
                resolve(true);
              };
              this.audio.addEventListener('loadedmetadata', onLoadedMetadata);
            });
          }
          
          duration = this.audio.duration || 0;
          elapsed = this.audio.currentTime || 0;
        } catch (error) {
          console.log('No se pudo obtener duración del episodio:', error);
        }
      }

      await CapacitorMusicControls.create({
        track: episode.title || 'Sin título', // required for iOS
        artist: episode.authorName || 'Artista desconocido', // required for iOS
        cover: episode.image, // optional, default : nothing
        // cover can be a local path (use fullpath 'file:///storage/emulated/...', or only 'my_image.jpg' if my_image.jpg is in the www folder of your app)
        //           or a remote url ('http://...', 'https://...', 'ftp://...')
        isPlaying: true, // optional, default : true
        dismissable: false, // optional, default : false

        // hide previous/next/close buttons:
        hasPrev: true, // show previous button, optional, default: true
        hasNext: true, // show next button, optional, default: true
        hasClose: true, // show close button, optional, default: false

        // iOS specific fields to avoid warnings
        album: episode.album || episode.title || 'Podcast', // iOS expects string, not null
        duration: duration || 0, // iOS expects number, not null
        elapsed: elapsed || 0, // iOS expects number, not null
        
        hasSkipForward: false, // show skip forward button, optional, default: false
        hasSkipBackward: false, // show skip backward button, optional, default: false
        // skipForwardInterval: 15, // display number for skip forward, optional, default: 0
        // skipBackwardInterval: 15, // display number for skip backward, optional, default: 0
        hasScrubbing: false, // enable scrubbing from control center and lockscreen progress bar, optional

        // Android only, optional
        // text displayed in the status bar when the notification (and the ticker) are updated, optional
        ticker: episode.title || 'Sin título',
        // All icons default to their built-in android equivalents
        playIcon: 'media_play',
        pauseIcon: 'media_pause',
        prevIcon: 'media_prev',
        nextIcon: 'media_next',
        closeIcon: 'media_close',
        notificationIcon: 'notification'
      });

      // Escuchar eventos del plugin
      CapacitorMusicControls.addListener('controlsNotification', (info) => {
        const message = info.action;
        switch (message) {
          case 'music-controls-next':
            this.playingEpisode.next$$.next(this.playingEpisode);
            break;
          case 'music-controls-previous':
            this.playingEpisode.previous$$.next(this.playingEpisode);
            break;
          case 'music-controls-pause':
            this.pause(this.playingEpisode);
            break;
          case 'music-controls-play':
            this.play(this.playingEpisode);
            break;
          case 'music-controls-destroy':
            this.destroyMusicControls();
            break;

          // External controls (iOS only)
          case 'music-controls-toggle-play-pause':
            if (this.audio && this.audio.paused) {
              this.play(this.playingEpisode);
            } else {
              this.pause(this.playingEpisode);
            }
            break;
          case 'music-controls-seek-to':
            const seekToInSeconds = info.position;
            this.seekTo(this.playingEpisode, seekToInSeconds);
            break;
          case 'music-controls-skip-forward':
            this.forward(this.playingEpisode, 15);
            break;
          case 'music-controls-skip-backward':
            this.rewind(this.playingEpisode, 15);
            break;

          // Headset events (Android only)
          // All media button events are listed below
          case 'music-controls-media-button':
            // Do something
            break;
          case 'music-controls-headset-unplugged':
            // Do something
            break;
          case 'music-controls-headset-plugged':
            // Do something
            break;
          default:
            break;
        }
      });

    } catch (error) {
      console.error('Error creating music controls:', error);
    }
  }

  async updateElapsed(elapsed: number, isPlaying: boolean) {
    try {
      await CapacitorMusicControls.updateElapsed({
        elapsed: elapsed,
        isPlaying: isPlaying
      });
    } catch (error) {
      console.error('Error updating elapsed:', error);
    }
  }

  async pause(episode) {
    console.log('PodcastService: Pausing episode:', episode.title);
    if (this.audio && !this.audio.paused) {
      console.log('PodcastService: Audio is playing, pausing...');
      this.audio.pause();
      console.log('PodcastService: Audio paused, paused state:', this.audio.paused);
      
      // Forzar el evento pause si no se dispara automáticamente
      setTimeout(() => {
        if (this.audio && this.audio.paused) {
          console.log('PodcastService: Forcing pause event');
          this.audio.dispatchEvent(new Event('pause'));
        }
      }, 50);
    } else {
      console.log('PodcastService: Audio is already paused or does not exist');
    }
    try {
      await CapacitorMusicControls.updateDismissable(true);
      await CapacitorMusicControls.updateIsPlaying({ isPlaying: false });
      console.log('PodcastService: Music controls updated for pause');
    } catch (error) {
      console.error('Error updating pause state:', error);
    }
  }

  async play(episode) {
    console.log('PodcastService: Playing episode:', episode.title);
    
    if (this.audio && this.audio.src !== episode.url) {
      console.log('PodcastService: Stopping previous audio');
      this.audio.pause();
      this.audio = null;
    }
    
    if (!this.audio) {
      console.log('PodcastService: Creating new audio');
      this.audio = this.create(episode);
    }

    try {
      console.log('PodcastService: Starting playback');
      
      // Si el audio ya está pausado, simplemente reanudar
      if (this.audio.paused) {
        await this.audio.play();
        console.log('PodcastService: Audio resumed from pause');
      } else {
        // Si no está pausado, crear controles y actualizar estado
        await this.audio.play();
        this.playingEpisode = episode;
        
        // Crear controles después de que el audio esté listo
        await this.createControls(episode);
        
        // Actualizar el estado de reproducción
        await CapacitorMusicControls.updateIsPlaying({ isPlaying: true });
        console.log('PodcastService: New audio started successfully');
      }
    } catch (error) {
      console.error('Error playing audio:', error);
    }
  }

  stop() {
    if (this.audio) {
      this.audio.pause();
      this.audio.currentTime = 0;
      this.audio = null;
    }
    this.destroyMusicControls();
  }

  async destroyMusicControls() {
    try {
      await CapacitorMusicControls.updateIsPlaying({ isPlaying: false });
      await CapacitorMusicControls.updateDismissable(true);
      await CapacitorMusicControls.destroy();
    } catch (error) {
      if (error.code !== 'UNIMPLEMENTED') {
        console.error('Error destroying music controls:', error);
      }
      // Si es UNIMPLEMENTED, lo ignoramos
    }
  }

  async rewind(episode, seconds: number) {
    if (this.audio) {
      this.audio.currentTime = Math.max(0, this.audio.currentTime - seconds);
    }
  }

  async forward(episode, seconds: number) {
    if (this.audio) {
      this.audio.currentTime = Math.min(this.audio.duration, this.audio.currentTime + seconds);
    }
  }

  async seekTo(episode, seconds: number) {
    if (this.audio) {
      this.audio.currentTime = seconds;
    }
  }

  // Métodos para obtener información del audio
  getCurrentPosition(): Promise<number> {
    return Promise.resolve(this.audio ? this.audio.currentTime : 0);
  }

  getDuration(): Promise<number> {
    return Promise.resolve(this.audio ? this.audio.duration : 0);
  }

  public getDataPodcast(id, filters: Filters) {
    return this.httpService.myGet(`${Environment.API_BASE_URL_V2}/api/empatica/v1/components/podcast/${id}/elements`, filters);
  }

  public getEpisode(id) {
    return this.httpService.myGet(`${Environment.API_BASE_URL_V2}/api/empatica/v1/components/podcast/episode/${id}`);
  }

  public like(idPage, idArticle) {
    return this.httpService.myPost(`${Environment.API_BASE_URL_V2}/api/empatica/v1/components/podcast/episode/${idArticle}/like`, {});
  }

  public unlike(idPage, idArticle) {
    return this.httpService.myPost(`${Environment.API_BASE_URL_V2}/api/empatica/v1/components/podcast/episode/${idArticle}/unlike`, {});
  }

  public getComments(idPage, idArticle, filters: Filters): any {
    return this.httpService.myGet(`${Environment.API_BASE_URL_V2}/api/empatica/v1/components/podcast/episode/${idArticle}/comments`, filters);
  }

  public sendComment(idPage, idArticle, comment) {
    return this.httpService.myPost(`${Environment.API_BASE_URL_V2}/api/empatica/v1/components/podcast/episode/${idArticle}/comments`, comment);
  }

  public removeComment(itemId, commentId) {
    return this.httpService.myDelete(`${Environment.API_BASE_URL_V2}/api/empatica/v1/components/podcast/episode/${itemId}/comments/${commentId}`);
  }
}
